import {
  ref, computed, watch, onBeforeUnmount,
} from 'vue';
import { useAnchoredPopover, activePopoverId } from './useAnchoredPopover';
import { fetchStatDefinitions, resolveBreakdown } from './useStatBreakdown';

let uid = 0;

// Hover, focus, click-to-pin and dismissal for a stat breakdown popover, shared by the
// classic StatBreakdown and the v5 one. props: { statKey, source, values }.
export default function useStatBreakdownPopover(props) {
  uid += 1;
  const id = `stat-breakdown-${uid}`;
  const triggerRef = ref(null);
  const popoverRef = ref(null);
  const pinned = ref(false);
  const pointerInPopover = ref(false);

  const {
    isOpen, style, open, close, scheduleOpen, scheduleClose, cancelClose,
  } = useAnchoredPopover(
    triggerRef,
    popoverRef,
    { fallbackWidth: 288 },
  );

  // Breakdown stays hidden until the definitions arrive; a failed fetch just
  // leaves the value unwrapped.
  fetchStatDefinitions().catch(() => {});

  const breakdown = computed(() => {
    if (!props.values) return null;
    return resolveBreakdown(props.statKey, { source: props.source, values: props.values });
  });

  function closeAll() {
    pinned.value = false;
    close();
  }

  function onTriggerEnter() {
    scheduleOpen();
  }

  // A blur while the pointer sits in the popover (e.g. selecting its numbers)
  // must not close it. Tabbing off a pinned trigger does, though: mouseleave is
  // what pinning defends against, and a keyboard user has nothing else to close it.
  function onTriggerLeave(event) {
    // document.hasFocus() is false when the whole window lost focus (alt-tab), as
    // opposed to focus moving on within the page — a pin must survive the former.
    if (event?.type === 'blur' && pinned.value && !pointerInPopover.value && document.hasFocus()) {
      closeAll();
      return;
    }
    if (!pinned.value && !pointerInPopover.value) scheduleClose();
  }

  function onPopoverEnter() {
    pointerInPopover.value = true;
    cancelClose();
  }

  function onPopoverLeave() {
    pointerInPopover.value = false;
    if (!pinned.value) scheduleClose();
  }

  function onTriggerClick() {
    if (pinned.value) {
      closeAll();
    } else {
      pinned.value = true;
      open();
    }
  }

  function onDocKeydown(event) {
    if (event.key === 'Escape') closeAll();
  }

  function onDocPointerdown(event) {
    const { target } = event;
    if (triggerRef.value?.contains(target) || popoverRef.value?.contains(target)) return;
    closeAll();
  }

  watch(isOpen, (open_) => {
    if (open_) {
      activePopoverId.value = id;
      document.addEventListener('keydown', onDocKeydown, true);
      document.addEventListener('pointerdown', onDocPointerdown, true);
    } else {
      pinned.value = false;
      pointerInPopover.value = false;
      if (activePopoverId.value === id) activePopoverId.value = null;
      document.removeEventListener('keydown', onDocKeydown, true);
      document.removeEventListener('pointerdown', onDocPointerdown, true);
    }
  });

  // Opening any other anchored popover closes this one.
  watch(activePopoverId, (current) => {
    if (current !== id && isOpen.value) closeAll();
  });

  // The recipe can disappear under an open popover (e.g. weapon filter change).
  watch(breakdown, (current) => {
    if (!current && isOpen.value) closeAll();
  });

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', onDocKeydown, true);
    document.removeEventListener('pointerdown', onDocPointerdown, true);
    if (activePopoverId.value === id) activePopoverId.value = null;
  });

  return {
    id,
    triggerRef,
    popoverRef,
    isOpen,
    style,
    breakdown,
    closeAll,
    onTriggerEnter,
    onTriggerLeave,
    onPopoverEnter,
    onPopoverLeave,
    onTriggerClick,
  };
}
